import { useCallback, useEffect, useRef, useState } from 'react';
import agent from '../../../../services/api/agent';
import { GOOGLE_TTS_MODEL, GoogleTtsModel, GoogleVoiceOption, LanguageOption } from '../types';
import { hasVoicesInCatalog, normalizeGoogleCatalog, resolveVoicesForLanguage, toLanguageOptions } from '../utils/voiceCatalog';
import { readGoogleVoiceCatalogCache, writeGoogleVoiceCatalogCache } from '../utils/voiceCatalogCache';

export type GoogleVoiceCatalogStatus = 'loading' | 'ready' | 'unavailable';

interface UseGoogleVoiceCatalogReturn {
  status: GoogleVoiceCatalogStatus;
  retrying: boolean;
  voiceError: string | null;
  voicesByLanguage: Record<string, GoogleVoiceOption[]>;
  languageOptions: LanguageOption[];
  getVoicesForLanguage: (languageCode: string) => GoogleVoiceOption[];
  refreshCatalog: () => Promise<boolean>;
}

const UNAVAILABLE_MESSAGE = 'Unable to load AI Voice 2.0 right now.';
const STALE_CACHE_MESSAGE = 'Using cached voice catalog. Some new voices may be unavailable.';

const useGoogleVoiceCatalog = (selectedModel: GoogleTtsModel = GOOGLE_TTS_MODEL): UseGoogleVoiceCatalogReturn => {
  const [voicesByLanguage, setVoicesByLanguage] = useState<Record<string, GoogleVoiceOption[]>>({});
  const [languageOptions, setLanguageOptions] = useState<LanguageOption[]>([]);
  const [status, setStatus] = useState<GoogleVoiceCatalogStatus>('loading');
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);

  const voicesRef = useRef<Record<string, GoogleVoiceOption[]>>({});

  useEffect(() => {
    voicesRef.current = voicesByLanguage;
  }, [voicesByLanguage]);

  const fetchFromNetwork = useCallback(async (): Promise<boolean> => {
    try {
      const response = await agent.Proxy.GetGoogleVoices('', selectedModel);
      const normalized = normalizeGoogleCatalog(response);
      const grouped = normalized.voicesByLanguage;
      const nextLanguageOptions = normalized.languageOptions.length > 0 ? normalized.languageOptions : toLanguageOptions(grouped);

      if (!hasVoicesInCatalog(grouped)) {
        throw new Error('No voices returned from endpoint.');
      }

      setVoicesByLanguage(grouped);
      setLanguageOptions(nextLanguageOptions);
      setStatus('ready');
      setVoiceError(null);
      writeGoogleVoiceCatalogCache(grouped, nextLanguageOptions, selectedModel);
      return true;
    } catch {
      if (hasVoicesInCatalog(voicesRef.current)) {
        setStatus('ready');
        setVoiceError(STALE_CACHE_MESSAGE);
        return false;
      }

      setStatus('unavailable');
      setVoiceError(UNAVAILABLE_MESSAGE);
      return false;
    }
  }, [selectedModel]);

  useEffect(() => {
    let active = true;

    const bootstrap = async () => {
      const cached = readGoogleVoiceCatalogCache(selectedModel);

      if (cached && active) {
        setVoicesByLanguage(cached.voicesByLanguage);
        setLanguageOptions(cached.languageOptions);
        setStatus('ready');

        if (cached.isFresh) {
          setVoiceError(null);
          return;
        }

        setVoiceError(STALE_CACHE_MESSAGE);
      }

      if (cached && cached.isFresh) {
        return;
      }

      if (!cached && active) {
        setStatus('loading');
      }

      const success = await fetchFromNetwork();
      if (!active) return;

      if (success) {
        setVoiceError(null);
      }
    };

    void bootstrap();

    return () => {
      active = false;
    };
  }, [fetchFromNetwork]);

  const refreshCatalog = useCallback(async () => {
    setRetrying(true);
    const result = await fetchFromNetwork();
    setRetrying(false);
    return result;
  }, [fetchFromNetwork]);

  const getVoicesForLanguage = useCallback((languageCode: string) => resolveVoicesForLanguage(voicesByLanguage, languageCode), [voicesByLanguage]);

  return {
    status,
    retrying,
    voiceError,
    voicesByLanguage,
    languageOptions,
    getVoicesForLanguage,
    refreshCatalog,
  };
};

export default useGoogleVoiceCatalog;
